import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { X, Check, ChevronRight, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { guidanceAPI } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';

const ROLE_GUIDES = {
  vendor: {
    title: 'Getting Started as a Seller',
    subtitle: 'Set up your shop in 3 steps',
    steps: [
      { key: 'profile_complete', label: 'Complete your profile', desc: 'Add a bio so buyers know who you are', link: '/settings', cta: 'Edit Profile' },
      { key: 'has_product', label: 'List your first product', desc: 'Add a product, set your price, go live', link: '/vendor', cta: 'Add Product', hash: '#add-product' },
      { key: 'has_project', label: 'Launch a funding project', desc: 'Get backed by fans while you build', link: '/projects/create', cta: 'Launch Project' },
    ],
  },
  creator: {
    title: 'Your Creator Journey',
    subtitle: 'From idea to funded in 3 steps',
    steps: [
      { key: 'profile_complete', label: 'Set up your artist profile', desc: 'Add a bio, genre, and links', link: '/settings', cta: 'Edit Profile' },
      { key: 'has_project', label: 'Launch your first project', desc: 'Set a goal, add tiers, go live', link: '/projects/create', cta: 'Launch Project' },
      { key: 'has_product', label: 'Sell merch or digital goods', desc: 'Monetize beyond funding', link: '/vendor', cta: 'Add Product' },
    ],
  },
  investor: {
    title: 'New to Backing Artists?',
    subtitle: 'Here\'s how it works',
    steps: [
      { key: 'profile_complete', label: 'Complete your profile', desc: 'Let creators know who\'s backing them', link: '/settings', cta: 'Edit Profile' },
      { key: 'has_investment', label: 'Back your first artist', desc: 'Start with $1 — pick someone you believe in', link: '/projects', cta: 'Browse Projects' },
    ],
  },
  buyer: {
    title: 'Welcome to MMarketing',
    subtitle: 'Discover what you can do here',
    steps: [
      { key: 'has_investment', label: 'Back an artist from $1', desc: 'Find rising talent before anyone else', link: '/projects', cta: 'Browse Projects' },
      { key: 'has_product', label: 'Find something to buy', desc: 'Deals, merch, and exclusive products', link: '/marketplace', cta: 'Browse Shop' },
    ],
  },
};

export default function GettingStarted({ pageContext }) {
  const { user } = useAuth();
  const [progress, setProgress] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchProgress = useCallback(async () => {
    try {
      const res = await guidanceAPI.getOnboarding();
      setProgress(res.data);
      setDismissed(res.data?.dismissed || false);
    } catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    fetchProgress();
  }, [user, fetchProgress]);

  const handleDismiss = async () => {
    setDismissed(true);
    try { await guidanceAPI.dismissOnboarding(); } catch {}
  };

  if (!user || loading || dismissed || !progress) return null;

  const role = progress.role || user.role || 'buyer';
  const guide = ROLE_GUIDES[role] || ROLE_GUIDES.buyer;
  const steps = progress.steps || {};

  // Filter to page-relevant steps or show all
  const visibleSteps = guide.steps;
  const completedCount = visibleSteps.filter(s => steps[s.key]).length;
  const allDone = completedCount === visibleSteps.length;

  // Don't show if all steps are done
  if (allDone) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, height: 0 }}
        className="mx-auto max-w-5xl px-4 pt-6 pb-2"
        data-testid="getting-started-guide"
      >
        <div className="rounded-2xl border border-zinc-200 bg-white p-5 relative">
          {/* Dismiss */}
          <button
            onClick={handleDismiss}
            className="absolute top-4 right-4 text-zinc-400 hover:text-zinc-600 transition-colors"
            data-testid="dismiss-guide-btn"
          >
            <X size={16} />
          </button>

          {/* Header */}
          <div className="flex items-center gap-3 mb-4">
            <div className="w-9 h-9 rounded-xl bg-[#b8ff00]/10 flex items-center justify-center">
              <Sparkles size={16} className="text-[#b8ff00]" />
            </div>
            <div>
              <h3 className="font-display font-bold text-base text-zinc-900">{guide.title}</h3>
              <p className="text-xs text-zinc-500">{guide.subtitle} — {completedCount}/{visibleSteps.length} done</p>
            </div>
          </div>

          {/* Progress bar */}
          <div className="h-1.5 bg-zinc-100 rounded-full mb-5 overflow-hidden">
            <motion.div
              className="h-full bg-[#b8ff00] rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${(completedCount / visibleSteps.length) * 100}%` }}
              transition={{ duration: 0.5 }}
            />
          </div>

          {/* Steps */}
          <div className="space-y-2">
            {visibleSteps.map((step, idx) => {
              const done = steps[step.key];
              return (
                <div
                  key={step.key}
                  className={`flex items-center gap-4 p-3 rounded-xl transition-all ${
                    done ? 'bg-zinc-50 opacity-60' : 'bg-zinc-50 hover:bg-zinc-100'
                  }`}
                  data-testid={`guide-step-${step.key}`}
                >
                  {/* Status circle */}
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                    done ? 'bg-emerald-100 text-emerald-600' : 'bg-zinc-200 text-zinc-500'
                  }`}>
                    {done ? <Check size={14} /> : <span className="text-xs font-bold">{idx + 1}</span>}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium ${done ? 'text-zinc-400 line-through' : 'text-zinc-900'}`}>{step.label}</p>
                    {!done && <p className="text-xs text-zinc-500">{step.desc}</p>}
                  </div>

                  {/* CTA */}
                  {!done && (
                    <Link
                      to={step.link}
                      className="flex items-center gap-1 px-3 py-1.5 bg-zinc-900 text-white text-xs font-medium rounded-lg hover:bg-zinc-800 transition-colors whitespace-nowrap"
                      data-testid={`guide-cta-${step.key}`}
                    >
                      {step.cta} <ChevronRight size={12} />
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
